import React, { useCallback, useContext, useEffect, useState } from "react"
import { Platform, Text, View } from "react-native"
import Loader from "../utility/loader"
import { extra, SERVER } from "../config"
import { ThemeContext } from "../utility/context/ThemeContext"
import { textStyles } from "../style"
import {
  CommunityImpactEmbedBody,
  COMMUNITY_EMBED_MIN_ROW_OUTER_WIDTH,
} from "./community-impact-embed-layout"
import type { CommunityImpactEmbedPayload } from "./community-impact-embed-layout"
import Analytics from "../utility/analytics"
import { log } from "../utility"
import * as Sentry from "../utility/sentry"

/** Added only to `oe-impact-resize-iframe` height (not merged into layout/scroll math). */
const COMMUNITY_IMPACT_EMBED_IFRAME_HEIGHT_PADDING_PX = 5

const EMBED_SCREEN_NAME = "embed/community-impact"

// Hosts (third-party FI sites) control the iframe URL and may append arbitrary query
// params — including PII, session tokens, or trackers. Only these documented keys are
// forwarded to analytics; everything else is dropped before reaching the pipeline.
const EMBED_QUERY_PARAM_ALLOWLIST: readonly string[] = ["theme"]

const readEmbedQueryParams = (): Record<string, string> | undefined => {
  if (typeof window === "undefined" || !window.location?.search) {
    return undefined
  }
  try {
    const params = new URLSearchParams(window.location.search)
    const filtered: Record<string, string> = {}
    for (const key of EMBED_QUERY_PARAM_ALLOWLIST) {
      const value = params.get(key)
      if (value !== null) {
        filtered[key] = value
      }
    }
    return Object.keys(filtered).length > 0 ? filtered : undefined
  } catch {
    return undefined
  }
}

const CommunityImpactEmbedding = (): React.ReactElement => {
  const containerRef = React.useRef(null)
  const parentOriginRef = React.useRef<string | null>(null)
  const lastPostedHeightRef = React.useRef(0)

  const [payload, setPayload] = useState<CommunityImpactEmbedPayload | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [containerWidth, setContainerWidth] = useState(0)
  const { theme } = useContext(ThemeContext)

  const useRowLayout =
    containerWidth > 0 && containerWidth >= COMMUNITY_EMBED_MIN_ROW_OUTER_WIDTH

  useEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") {
      return
    }
    if (!window.parent || window.parent === window) {
      return
    }

    const sendScrollToParent = (deltaX: number, deltaY: number): void => {
      if (deltaX === 0 && deltaY === 0) {
        return
      }
      const targetOrigin = parentOriginRef.current || "*"
      window.parent.postMessage(
        {
          type: "oe-impact-parent-scroll",
          deltaX,
          deltaY,
        },
        targetOrigin
      )
    }

    /**
     * RN Web often scrolls inner nodes, not document.documentElement, so “scroll chaining”
     * heuristics never fired. For this embed, always forward wheel / single-finger pan to the
     * parent page so the host can scroll while the pointer is over the iframe.
     */
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault()
      e.stopPropagation()
      sendScrollToParent(e.deltaX, e.deltaY)
    }

    let touchLastY: number | null = null

    const onTouchStart = (e: TouchEvent): void => {
      if (e.touches.length === 1) {
        touchLastY = e.touches[0].clientY
      } else {
        touchLastY = null
      }
    }

    const onTouchMove = (e: TouchEvent): void => {
      if (e.touches.length !== 1 || touchLastY === null) {
        return
      }
      const cy = e.touches[0].clientY
      const deltaY = touchLastY - cy
      touchLastY = cy
      if (deltaY === 0) {
        return
      }
      e.preventDefault()
      e.stopPropagation()
      sendScrollToParent(0, deltaY * 1.25)
    }

    const onTouchEnd = (): void => {
      touchLastY = null
    }

    const wheelOpts: AddEventListenerOptions = { capture: true, passive: false }
    const touchMoveOpts: AddEventListenerOptions = { capture: true, passive: false }
    const touchPassiveOpts: AddEventListenerOptions = { capture: true, passive: true }

    document.addEventListener("wheel", onWheel, wheelOpts)
    document.addEventListener("touchstart", onTouchStart, touchPassiveOpts)
    document.addEventListener("touchmove", onTouchMove, touchMoveOpts)
    document.addEventListener("touchend", onTouchEnd, touchPassiveOpts)
    document.addEventListener("touchcancel", onTouchEnd, touchPassiveOpts)

    return () => {
      document.removeEventListener("wheel", onWheel, true)
      document.removeEventListener("touchstart", onTouchStart, true)
      document.removeEventListener("touchmove", onTouchMove, true)
      document.removeEventListener("touchend", onTouchEnd, true)
      document.removeEventListener("touchcancel", onTouchEnd, true)
    }
  }, [])

  useEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") {
      return
    }

    const handleMessage = (event: MessageEvent) => {
      if (
        event.source !== window.parent ||
        event.data?.type !== "oe-impact-init-iframe"
      ) {
        return
      }

      const { origin, data: initData } = event

      if (!initData || !initData.customerId) {
        log.error(new Error("Embed init message missing customerId"), {
          tags: { message: "community-impact embed init missing customerId" },
          extra: {
            screen: EMBED_SCREEN_NAME,
            parentOrigin: origin,
            hasInitData: !!initData,
          },
        })
        setErrorMessage("Failed to initialize")
        return
      }

      parentOriginRef.current = origin

      fetchData(initData.customerId)
    }

    window.addEventListener("message", handleMessage)

    window.parent.postMessage(
      { type: "oe-impact-ready" },
      "*"
    )

    return () => window.removeEventListener("message", handleMessage)
  }, [])

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!parentOriginRef.current && loading && !payload && !errorMessage) {
        log.error(new Error("Embed init timed out: no host init message received"), {
          tags: { message: "community-impact embed init timeout" },
          extra: {
            screen: EMBED_SCREEN_NAME,
            timeoutMs: 5000,
            parentUrl: typeof document !== "undefined" ? document.referrer || undefined : undefined,
          },
        })
        setErrorMessage("Failed to initialize")
      }
    }, 5000)

    return () => clearTimeout(timeout)
  }, [])

  // Fire a single embed_view event when the iframe mounts so we can count
  // how many host pages have loaded this embed. No duration, no follow-ups.
  useEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined" || typeof document === "undefined") {
      return
    }
    // Tag every Sentry event from this iframe with the embed screen so embed
    // failures are easy to filter from the in-app traffic.
    Sentry.setTag("screen", EMBED_SCREEN_NAME)
    try {
      Analytics.track<"embed_view">("embed_view", {
        screen: EMBED_SCREEN_NAME,
        parentUrl: document.referrer || undefined,
        query: readEmbedQueryParams(),
        env: extra.env,
      })
      Analytics.flush()
    } catch {
      // Analytics is best-effort; never break the embed UI.
    }
  }, [])

  const sendHeight = useCallback((layoutHeightPx: number): void => {
    const origin = parentOriginRef.current
    if (!origin) {
      return
    }

    let contentHeightPx = Math.round(Math.max(0, layoutHeightPx))

    if (Platform.OS === "web" && typeof document !== "undefined") {
      const node = containerRef.current as unknown as HTMLElement | null
      const containerMeasured =
        node && typeof node.getBoundingClientRect === "function"
          ? Math.ceil(node.getBoundingClientRect().height)
          : 0

      if (containerMeasured > 0) {
        contentHeightPx = Math.max(contentHeightPx, containerMeasured)
      } else {
        const docEl = document.documentElement
        const body = document.body
        const scrollBound = Math.ceil(
          Math.max(docEl.scrollHeight, body?.scrollHeight ?? 0)
        )
        contentHeightPx = Math.max(contentHeightPx, scrollBound)
      }
    }

    contentHeightPx = Math.max(1, contentHeightPx)
    const iframeHeightPx =
      contentHeightPx + COMMUNITY_IMPACT_EMBED_IFRAME_HEIGHT_PADDING_PX

    if (iframeHeightPx === lastPostedHeightRef.current) {
      return
    }
    lastPostedHeightRef.current = iframeHeightPx

    window.parent.postMessage(
      { type: "oe-impact-resize-iframe", height: iframeHeightPx },
      origin
    )
  }, [])

  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined") {
      return
    }
    if (typeof ResizeObserver === "undefined") {
      return
    }
    const root = document.getElementById("root")
    if (!root) {
      return
    }

    let rafId: number | null = null
    const scheduleMeasure = (): void => {
      if (rafId != null) {
        return
      }
      rafId = requestAnimationFrame(() => {
        rafId = null
        sendHeight(0)
      })
    }

    const ro = new ResizeObserver(() => {
      scheduleMeasure()
    })
    ro.observe(root)
    scheduleMeasure()

    return () => {
      ro.disconnect()
      if (rafId != null) {
        cancelAnimationFrame(rafId)
      }
    }
  }, [sendHeight, loading, payload, errorMessage, containerWidth, useRowLayout])

  const fetchData = async (customerId: string) => {
    try {
      if (!customerId) {
        log.error(new Error("Embed community-impact fetch failed: customerId is required"), {
          tags: { message: "community-impact embed fetch customerId required" },
          extra: {
            screen: EMBED_SCREEN_NAME
          },
        })
        setErrorMessage("Failed to initialize")
        setPayload(null)
        setLoading(false)
        return
      }

      const normalizedCustomerId = customerId.trim()
      if (!normalizedCustomerId) {
        setErrorMessage("Failed to initialize")
        setPayload(null)
        setLoading(false)
        return
      }
      const safeCustomerId = encodeURIComponent(normalizedCustomerId)
      const response = await fetch(`${SERVER}/api/community-impact/${safeCustomerId}`)
      const json = await response.json()

      if (!response.ok) {
        const serverMessage = typeof json?.message === "string" ? json.message : "Request failed"
        log.error(new Error(`Embed community-impact fetch failed: ${response.status}`), {
          tags: { message: "community-impact embed fetch non-OK response" },
          extra: {
            screen: EMBED_SCREEN_NAME,
            status: response.status,
            serverMessage,
          },
        })
        setErrorMessage(serverMessage)
        setPayload(null)
        return
      }
      setErrorMessage(null)
      setPayload(json as CommunityImpactEmbedPayload)
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Request failed"
      log.error(error instanceof Error ? error : new Error(message), {
        tags: { message: "community-impact embed fetch threw" },
        extra: {
          screen: EMBED_SCREEN_NAME
        },
      })
      setErrorMessage(message)
      setPayload(null)
    } finally {
      if (extra.env === "local") {
        await new Promise(resolve => setTimeout(resolve, 3_000))
      }
      setLoading(false)
    }
  }

  if (errorMessage) {
    return (
      <View style={{ width: "100%", alignItems: "center", padding: 20, backgroundColor: theme.colors.bgMain }}>
        <Text style={[textStyles.h3, { color: theme.colors.danger }]}>
          Something went wrong
        </Text>
        <Text style={[textStyles.body, { color: theme.colors.textMain }]}>{errorMessage}</Text>
      </View>
    )
  }

  return (
    <View
      ref={containerRef}
      style={{ backgroundColor: theme.colors.bgMain, padding: 16, width: "100%" }}
      onLayout={(e) => {
        const { width, height } = e.nativeEvent.layout
        const w = Math.round(width)
        const h = Math.round(height)
        if (w !== containerWidth) {
          setContainerWidth(w)
        }
        sendHeight(h)
      }}
    >
      {loading || !payload ? (
        <View style={{ flex: 1, flexDirection: "column", alignItems: "center", gap: 15 }}>
          <Loader size="large" />
          <Text style={[textStyles.body, { color: theme.colors.textMain }]}>
            Fetching Impact Data
          </Text>
        </View>
      ) : (
        <CommunityImpactEmbedBody data={payload} useRowLayout={useRowLayout} />
      )}
    </View>
  )
}

export default CommunityImpactEmbedding

/*
Example of how to use this component:

<script type="application/javascript">
  window.addEventListener("message", function (event) {
    const iframe = document.getElementById("oe-impact-chart-iframe");
    if (event.data?.type === "oe-impact-resize-iframe") {
      iframe.style.height = `${event.data.height}px`;
    }
    if (event.data?.type === "oe-impact-parent-scroll") {
      const dx = typeof event.data.deltaX === "number" ? event.data.deltaX : 0;
      const dy = typeof event.data.deltaY === "number" ? event.data.deltaY : 0;
      window.scrollBy(dx, dy);
    }
    if (event.data?.type === "oe-impact-ready") {
      iframe.contentWindow.postMessage(
        { type: "oe-impact-init-iframe", customerId: "{CUSTOMER_ID}" },
        "{WEBAPP_ORIGIN}"
      );
    }
  });
</script>

<iframe
  id="oe-impact-chart-iframe"
  src="{WEBAPP_ORIGIN}/web/community-impact?theme=climateFirst"
  width="100%"
  style="border: none;"
></iframe>

*/
